import { Box, ShoppingCart } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, LinkButton } from "../components/Button";
import { EmptyState, SectionHeader } from "../components/UI";
import { apiFetch, formatCurrency, getProducts, resolveAssetUrl, type Product } from "../lib/api";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

export function ProductsPage() {
  const { addProductToCart, cart } = useAppState();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [open3dIds, setOpen3dIds] = useState<number[]>([]);
  const [title, setTitle] = useState("Our 3D Printable Models");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch<{ title?: string }>("/products/config").catch(() => ({ title: "Our 3D Printable Models" })),
      getProducts()
    ])
      .then(([config, data]) => {
        if (cancelled) return;
        setTitle(config.title || "Our 3D Printable Models");
        setProducts(data.filter((product) => product.is_active !== false));
      })
      .catch(() => {
        if (!cancelled) setError("Products could not be loaded. Check that Django is running.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle3dPreview(productId: number, event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setOpen3dIds((current) =>
      current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId]
    );
  }

  function cartQuantityFor(product: Product) {
    const colorKey = (product.color_options?.[0]?.label || "default").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const itemId = `product-${product.id}-${colorKey}`;
    return cart.find((item) => item.source === "product" && item.id === itemId)?.quantity || 0;
  }

  return (
    <section className="products-page">
      <SectionHeader title={<span id="products-page-title">{title}</span>} className="products-page-header" />

      {loading ? <p className="muted">Loading products…</p> : null}
      {error ? (
        <EmptyState icon={<Box size={24} />} action={<LinkButton to="/quote">Get A Custom Quote</LinkButton>}>
          {error}
        </EmptyState>
      ) : null}
      {!loading && !error && !products.length ? (
        <EmptyState icon={<Box size={24} />} action={<LinkButton to="/quote">Get A Custom Quote</LinkButton>}>
          No products are available yet.
        </EmptyState>
      ) : null}

      <div className="products-grid">
        {products.map((product) => {
          const cartQuantity = cartQuantityFor(product);
          return (
          <article
            className="product-card"
            key={product.id}
            role="link"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/products/${product.id}`)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate(`/products/${product.id}`);
              }
            }}
          >
            <div className="product-card-img" aria-label={`View ${product.name}`} onDragStart={(event) => event.preventDefault()}>
              {product.stl_file ? (
                <button
                  type="button"
                  className="product-card-3d-toggle"
                  aria-label={open3dIds.includes(product.id) ? `Hide 3D View For ${product.name}` : `View 3D Model For ${product.name}`}
                  onClick={(event) => toggle3dPreview(product.id, event)}
                >
                  <Box size={15} aria-hidden="true" />
                  {open3dIds.includes(product.id) ? "Image" : "3D"}
                </button>
              ) : null}
              {product.image && !open3dIds.includes(product.id) ? (
                <img src={resolveAssetUrl(product.image)} alt={product.name} width={420} height={320} loading="lazy" draggable={false} />
              ) : product.stl_file && open3dIds.includes(product.id) ? (
                <Suspense fallback={<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)", fontSize: "0.82rem" }}>Loading STL...</div>}>
                  <STLViewer
                    url={resolveAssetUrl(product.stl_file)}
                    color="Blue"
                    mode="product"
                    interactive
                    autoRotate
                    className="stl-viewer-preview"
                  />
                </Suspense>
              ) : product.stl_file ? (
                <div className="product-card-3d-placeholder">
                  <Box size={36} aria-hidden="true" />
                  <span>3D preview available</span>
                </div>
              ) : (
                <Box size={40} aria-hidden="true" />
              )}
            </div>
            <div className="product-card-body">
              <h3>{product.name}</h3>
              <p className="product-card-sub">{product.subtitle || product.description || "Ready-to-print model from the ProtoDeccan catalog."}</p>
              <div className="product-footer">
                <strong className="product-card-price">{formatCurrency(product.price)}</strong>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<ShoppingCart size={16} />}
                  className="product-card-add-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    addProductToCart(product, 1, "Standard Product", product.color_options?.[0]?.label, product.color_options?.[0]?.hex);
                  }}
                  >
                  {cartQuantity ? `Add more (${cartQuantity})` : "Add"}
                </Button>
              </div>
            </div>
          </article>
        );
        })}
      </div>
    </section>
  );
}
